import { type CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import React, { type ChangeEvent, useState, type FC, memo, forwardRef, type RefObject } from 'react';
import { type GeneralFieldProps } from '../../../types/Field';
import { captureIfThrows } from '../../../utils/errorReporter';
import { getMaxCardLengthByNumberToFocusNext, maxCardNumberLength } from '../../CardForm/utils/cardNetworksMeta';
import { formatCardNumber } from '../../CardForm/utils/formatting';
import { sanitizeCardNumber } from '../../CardForm/utils/sanitizeUtils';
import { CardNumberView } from './view';

type CardNumberControllerProps = GeneralFieldProps & {
  cardNetwork: CreditCardNetwork;
  cardNetworkBrandsList: CreditCardNetwork[];
};

export const CardNumberController: FC<CardNumberControllerProps> = memo(
  forwardRef((props: CardNumberControllerProps, ref: RefObject<HTMLInputElement>) => {
    const { value, cardNetworkBrandsList, onChange } = props;
    // ChangeEvent.inputType isn't supported in IE11, thus tracking manually
    const [lastKey, setLastKey] = useState<string>();
    const formattedValue: string = formatCardNumber(value);

    const handleChange = captureIfThrows((e: ChangeEvent<HTMLInputElement>) => {
      // prev value -React process-> event value & caret -calc-> next value and caret
      const inputElement: HTMLInputElement = e.currentTarget;
      const prevValue: string = value;
      const prevFormattedValue: string = formattedValue;
      const eventFormattedValue: string = e.target.value;
      const eventFinalCaretPos: number = inputElement.selectionStart;
      let nextFormattedValue: string = formatCardNumber(eventFormattedValue);
      let nextCaretPos: number = eventFinalCaretPos;

      // char added
      if (eventFormattedValue.length - prevFormattedValue.length === 1) {
        if (prevValue.length === maxCardNumberLength) {
          // ignore any change if already max length card number
          nextFormattedValue = prevFormattedValue;
        }

        if (nextFormattedValue === prevFormattedValue) {
          // React automatically increments caret pos, even if we'll ignore the change
          // thus need to counter-decrement
          nextCaretPos -= 1;
        } else if (eventFinalCaretPos === eventFormattedValue.length) {
          nextCaretPos = nextFormattedValue.length;
        } else if (eventFormattedValue.charAt(eventFinalCaretPos) === ' ') {
          nextCaretPos += 1;
        }
      } else if (
        prevFormattedValue.length - eventFormattedValue.length === 1 &&
        ['Backspace', 'Delete'].includes(lastKey) &&
        // space was removed
        prevFormattedValue[eventFinalCaretPos] === ' ' &&
        eventFormattedValue[eventFinalCaretPos] !== ' '
      ) {
        const extraCharToRemoveI: number = lastKey === 'Backspace' ? eventFinalCaretPos - 1 : eventFinalCaretPos;

        nextFormattedValue = eventFormattedValue
          .split('')
          .filter((char, i) => i !== extraCharToRemoveI)
          .join('');

        if (nextCaretPos < nextFormattedValue.length && lastKey === 'Backspace') {
          nextCaretPos -= 1;
        }
      } else if (
        // when value is pasted
        eventFormattedValue.length - prevFormattedValue.length > 1 &&
        // When formatting separate groups of digits with spaces
        nextFormattedValue.split(' ').length > 1 &&
        nextFormattedValue.length === nextFormattedValue.split(' ').length - 1 + nextCaretPos
      ) {
        nextCaretPos += nextFormattedValue.split(' ').length - 1;
      }

      const nextValue: string = sanitizeCardNumber(nextFormattedValue);

      onChange(
        nextValue,
        captureIfThrows(() => {
          if (inputElement.setSelectionRange && document.activeElement === inputElement) {
            inputElement.setSelectionRange(nextCaretPos, nextCaretPos);
          }
        }),
        nextValue.length === getMaxCardLengthByNumberToFocusNext(nextValue),
      );
    });

    return (
      <CardNumberView
        {...props}
        cardNetworkBrandsList={cardNetworkBrandsList}
        ref={ref}
        onKeyDown={(e) => {
          setLastKey(e.key);
        }}
        onChange={handleChange}
      />
    );
  }),
);
CardNumberController.displayName = 'CardNumberController';
